<template>
    <DemoToolsLauncher ref="launcher" title="Perspective demo tools">
        <div class="flex flex-col gap-4">
            <h2 class="body-small-bold text-valence-stone-900">Perspective demo tools</h2>

            <div v-if="sessionId" class="flex flex-col gap-2">
                <p class="details text-valence-stone-500">Answers what you've left open and skips to the end. Your existing answers stay put.</p>
                <BaseButton
                    theme="primary"
                    size="sm"
                    class="action"
                    :disabled="busyAction !== null"
                    aria-label="Fill the remaining answers and skip to the end"
                    @click="fillRemaining">
                    <i class="bi bi-magic" aria-hidden="true"></i>
                    <span>{{ busyAction === "fill" ? "Filling…" : "Fill remaining answers" }}</span>
                </BaseButton>
            </div>

            <div v-else class="flex flex-col gap-2">
                <p class="details text-valence-stone-500">Completes a Perspective in your name with synthetic answers and opens your results.</p>
                <BaseButton theme="primary" size="sm" class="action" :disabled="busyAction !== null" aria-label="Create a completed Perspective" @click="bootstrap">
                    <i class="bi bi-stars" aria-hidden="true"></i>
                    <span>{{ busyAction === "bootstrap" ? "Completing…" : "Completed Perspective" }}</span>
                </BaseButton>
            </div>

            <p v-if="inlineError" class="details text-status-error" role="alert">{{ inlineError }}</p>
        </div>
    </DemoToolsLauncher>
</template>

<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { FillRemainingResponsesResponse, PerspectiveResult } from "~vue/api/generated/perspectives";
import BaseButton from "~vue/components/BaseButton.vue";
import DemoToolsLauncher from "~vue/DemoTools/DemoToolsLauncher.vue";
import { demoToolsErrorMessage } from "~vue/DemoTools/errorMessage";
import { ref, useTemplateRef } from "vue";

interface Props {
    sessionId?: string;
}

const { sessionId } = defineProps<Props>();
const emit = defineEmits<{ filled: [responses: Record<string, number>] }>();

const launcher = useTemplateRef("launcher");
const busyAction = ref<"fill" | "bootstrap" | null>(null);
const inlineError = ref<string | null>(null);

function errorMessage(statusCode: number | null, serverMessage: string, fallback: string): string {
    switch (statusCode) {
        case 404:
            return "This Perspective session no longer exists.";
        case 409:
            return "This Perspective has already been submitted.";
        default:
            return demoToolsErrorMessage(statusCode, serverMessage, fallback);
    }
}

async function fillRemaining(): Promise<void> {
    if (!sessionId || busyAction.value !== null) {
        return;
    }
    busyAction.value = "fill";
    inlineError.value = null;
    const { data, error, statusCode } = await useFetch(`/v1/perspectives/sessions/${sessionId}/fill-remaining`).post().json<FillRemainingResponsesResponse>();
    busyAction.value = null;

    if (error.value) {
        inlineError.value = errorMessage(statusCode.value, error.value.message, "Could not fill the remaining answers.");
        return;
    }

    const filled = data.value?.filled_responses ?? {};
    const count = Object.keys(filled).length;
    emit("filled", filled);
    launcher.value?.finish(
        count === 0
            ? { theme: "info", text: "Nothing left to fill — skipped you to the end." }
            : { theme: "success", text: `Filled ${count} ${count === 1 ? "answer" : "answers"} and skipped to the end. Submit when you're ready.` },
    );
}

async function bootstrap(): Promise<void> {
    if (busyAction.value !== null) {
        return;
    }
    busyAction.value = "bootstrap";
    inlineError.value = null;
    const { error, statusCode } = await useFetch("/v1/perspectives/demo/bootstrap").post().json<PerspectiveResult>();
    busyAction.value = null;

    if (error.value) {
        inlineError.value = errorMessage(statusCode.value, error.value.message, "Could not create the demo Perspective.");
        return;
    }

    launcher.value?.finish({ theme: "success", text: "Completed a Perspective in your name. Opening your results." });
    router.visit("/perspective/");
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .action {
        @apply inline-flex w-full items-center justify-center gap-2;
    }
}
</style>
